import { fetchJSON as fetchAPI } from './client';

export type AssetGridRow = {
  asset_id: string;
  name: string;
  type: string;
  status: string;
  asin?: string;
  product_id?: string;
  quality_score: number;
  brand_aligned: boolean;
  marketplace_ok: boolean;
  prompt_version?: number;
  provider?: string;
  updated_at: string;
};

export type DashboardSummary = {
  tenant_id: string;
  workspace_id?: string;
  total_assets: number;
  pending_review: number;
  approved_assets: number;
  published_assets: number;
  avg_quality_score: number;
  pending_bulk_jobs: number;
  unread_notifications: number;
  indexed_items: number;
  by_status?: Record<string, number>;
  by_type?: Record<string, number>;
  provider_health?: Record<string, string>;
  recent_activity?: ActivityEntry[];
  top_assets?: AssetGridRow[];
  generated_at: string;
};

export type CreativeAsset = {
  id: string;
  workspace_id: string;
  name: string;
  type: string;
  status: string;
  asin?: string;
  asset_url?: string;
  provider?: string;
  prompt_version?: number;
  validation: {
    valid: boolean;
    brand_consistent: boolean;
    marketplace_compliance_ok: boolean;
    issues?: string[];
  };
  quality: {
    clarity: number;
    composition: number;
    overall_score: number;
    brand_alignment: number;
    marketplace_readiness: number;
  };
  updated_at: string;
};

export type AssetComparison = {
  asset_id: string;
  field: string;
  current: string;
  previous: string;
  version: number;
  quality?: QualityExplanation;
};

export type QualityExplanation = {
  summary: string;
  factors?: { factor: string; description: string; score: number; weight: number }[];
  issues?: string[];
};

export type ApprovalWorkflow = {
  id: string;
  asset_id?: string;
  state: string;
  publish_ready: boolean;
  steps: { name: string; state: string; message?: string }[];
};

export type SearchResult = {
  assets?: CreativeAsset[];
  total: number;
  facets?: { status?: Record<string, number>; creative_type?: Record<string, number>; quality_band?: Record<string, number> };
  took_ms?: number;
};

export type ActivityEntry = {
  id: string;
  resource_id?: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type CreativeNotification = {
  id: string;
  resource_id?: string;
  title: string;
  body?: string;
  priority: string;
  read: boolean;
  created_at: string;
};

export type BulkOperation = {
  id: string;
  action: string;
  state: string;
  asset_ids?: string[];
  progress: number;
  processed: number;
  failed: number;
  total: number;
  created_at: string;
};

export type PromptTemplate = {
  id: string;
  name: string;
  version: number;
  template: string;
  creative_type?: string;
  active: boolean;
};

const API_BASE = import.meta.env.VITE_AI_CREATIVE_API ?? '/api/v1/ai/creative';

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const aiCreativeApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<DashboardSummary>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  assetGrid: (workspaceId?: string, limit = 50, offset = 0) =>
    fetchJSON<{ rows: AssetGridRow[]; total: number }>(
      `/grid?workspace_id=${workspaceId ?? ''}&limit=${limit}&offset=${offset}`,
    ),
  createAsset: (body: { workspace_id: string; name: string; type: string; asin?: string; marketplace_id?: string }) =>
    fetchJSON<CreativeAsset>('/assets', { method: 'POST', body: JSON.stringify(body) }),
  getAsset: (id: string) => fetchJSON<CreativeAsset>(`/assets/${id}`),
  generate: (id: string, prompt: string) =>
    fetchJSON<CreativeAsset>(`/assets/${id}/generate`, {
      method: 'POST',
      body: JSON.stringify({ request: { operation: 'generate', prompt } }),
    }),
  validate: (id: string) => fetchJSON<{ valid: boolean; issues?: string[] }>(`/assets/${id}/validate`, { method: 'POST' }),
  transition: (id: string, status: string) =>
    fetchJSON<CreativeAsset>(`/assets/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  globalSearch: (text: string, workspaceId?: string) =>
    fetchJSON<SearchResult>('/search/global', {
      method: 'POST',
      body: JSON.stringify({ text, workspace_id: workspaceId ?? '', limit: 50 }),
    }),
  compare: (assetId: string) => fetchJSON<AssetComparison[]>(`/assets/${assetId}/compare`),
  explain: (assetId: string) => fetchJSON<QualityExplanation>(`/assets/${assetId}/explain`),
  workflow: (assetId: string) => fetchJSON<ApprovalWorkflow>(`/assets/${assetId}/workflow`),
  listPrompts: (workspaceId?: string) =>
    fetchJSON<PromptTemplate[]>(`/prompts?workspace_id=${workspaceId ?? ''}`),
  createPrompt: (body: { workspace_id: string; name: string; template: string; variables?: string[]; creative_type?: string }) =>
    fetchJSON<PromptTemplate>('/prompts', { method: 'POST', body: JSON.stringify(body) }),
  submitFeedback: (body: { asset_id: string; rating: string; comment?: string }) =>
    fetchJSON<{ id: string }>('/feedback', { method: 'POST', body: JSON.stringify(body) }),
  triggerBulk: (body: { action: string; asset_ids: string[]; workspace_id: string }) =>
    fetchJSON<BulkOperation>('/bulk/ops', { method: 'POST', body: JSON.stringify(body) }),
  listActivity: (resourceId?: string, limit = 50) =>
    fetchJSON<ActivityEntry[]>(`/activity${resourceId ? `?resource_id=${resourceId}&limit=${limit}` : `?limit=${limit}`}`),
  listNotifications: (unread = false) =>
    fetchJSON<CreativeNotification[]>(`/notifications?unread=${unread}`),
  diagnostics: () => fetchJSON<Record<string, unknown>>('/diagnostics'),
};

export const BULK_ACTIONS = ['generate', 'validate', 'approve', 'archive'] as const;
export const ASSET_STATUSES = ['draft', 'generated', 'under_review', 'approved', 'rejected', 'published', 'archived'] as const;
export const CREATIVE_TYPES = ['main_image', 'lifestyle', 'infographic', 'aplus_content', 'brand_asset'] as const;
